import React, { useState } from 'react';
import AccountSimplified from 'features/accounts/components/AccountSimplified';
import { useStateDataGrid } from 'features/shared/components/dataGrid/hooks/useStateDataGrid'
import { useQuery } from 'data/hooks/useQuery';
import DataQuery from 'data/DataQuery';
import { Accounts_DeleteAccount, Accounts_InsertAccount, Customer_Accounts } from 'data/endpoints';
import { useSelector } from 'react-redux';
import IAppState from 'infrastructure/store/IAppState';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import AccountSimplifiedDTO from 'features/accounts/dtos/AccountSimplifiedDTO';
import DataFilterModal from 'features/shared/components/dataFilterModal/DataFilterModal';
import DataGridHeader from 'features/shared/components/dataGrid/DataGridHeader';
import DataGridRow from 'features/shared/components/dataGrid/DataGridRow';
import AccountSimplifiedHeader from 'features/accounts/components/AccountSimplifiedHeader';
import InsertAccountDialog from 'features/accounts/components/insertAccountDialog/InsertAccountDialog';
import AccountFullDTO from 'features/accounts/dtos/AccountFullDTO';
import PostResponse from 'data/PostResponse';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import IRepository from 'data/IRepository';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import { trackPromise } from 'react-promise-tracker';
import { Accounts_InsertAccountDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import { useAccountRestrictionsData } from 'features/accounts/hooks/useAccountRestrictionsData';
import { loadDataFilterConfig } from 'features/shared/helpers/modules';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import Alert from 'features/shared/alerts/components/Alert';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import ContentDialogConfirm from 'features/shared/components/contentDialog/ContentDialogConfirm';
import { useTranslation } from 'react-i18next';
import { getForAdminParam } from 'features/shared/helpers/queryParams';

const accountsDataFilterJson = loadDataFilterConfig("accountsDataFilter");
const accountsCount = 50;

interface Props {
  customerId: string;
  forAdminUse?: boolean;
  onAccountDetailClick?: (account: AccountSimplifiedDTO) => void;
}

const AccountsSection = ({ customerId, forAdminUse = false, onAccountDetailClick }: Props) => {
  //#region translations
  const tLabels = useTranslation("accounts\\accounts").t;
  //#endregion translations

  //#region DI
  const accountRepository = useInjection<IRepository<AccountFullDTO>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService); 
  //#endregion DI

  //#region STATE
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [modifiedAccounts, setModifiedAccounts] = useState(0);
  //#endregion STATE

  //#region GRID HOOK
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    filterApplyHandler,
    filterResetHandler,
    isApplyFilter,
    conditionalRefresh
  } = useStateDataGrid(undefined, accountsDataFilterJson);
  //#endregion GRID HOOK

  //#region HELPERS
  const getParamsWithCount = (computedParams: string, count: number): URLSearchParams => {
    let urlParams = new URLSearchParams(computedParams);
    urlParams.set("Count", String(count));

    if (forAdminUse) {
      urlParams.set("ForAdminUse", forAdminUse.toString());
    }

    return urlParams;
  }
  //#endregion HELPERS
  
  //#region FETCH DATA 
  // accounts
  const query: DataQuery = {
    url: Customer_Accounts,
    routeParams: getRouteParams(
      "customerId",
      customerId
    ),
    params: getParamsWithCount(dataGrid.computedUrlSearchParams, accountsCount)
  };
  // conditionalExecute (conditionalRefresh) neni potreba, protoze se nepouziva strankovani ani trizeni (a filtr je v defaultu prazdny)
  const { data: accounts, loadStatus: accountsStatus } = useQuery<AccountSimplifiedDTO[]>(query, [], [dataGrid.computedUrlSearchParams, modifiedAccounts]);
  
  // contracts, contractual accounts
  const { contracts, contractualAccounts } = useAccountRestrictionsData(customerId, showAccountDialog, forAdminUse);
  //#endregion FETCH DATA

  //#region HANDLERS
  const submitInsertAccountDialogHandler = async (account: AccountFullDTO): Promise<PostResponse<string>> => {
    let response: PostResponse<string>;
    
    if (accounts.some(x => x.email === account.email)) {
      response = { status: 400, data: tLabels("accountSection.duplicateEmailMessage") }
    } else {
      response = await trackPromise(
        accountRepository.post<string>({
          url: Accounts_InsertAccount,
          data: account,
          params: getForAdminParam(forAdminUse)
        }),
        Accounts_InsertAccountDialog
      );

      if (response.status == 200) {
        setModifiedAccounts(modifiedAccounts + 1);
      }
    }

    return response;
  }

  const deleteAccountHandler = async (account: AccountSimplifiedDTO) => {
    let response = await trackPromise(
      accountRepository.delete({
        url: Accounts_DeleteAccount,
        routeParams: getRouteParams("accountId", account.accountId!),
        params: getForAdminParam(forAdminUse)
      })
    );

    if (response.status == 204) {
      setModifiedAccounts(modifiedAccounts + 1);
    }
  }

  const confirmDeleteAccountHandler = (account: AccountSimplifiedDTO) => {
    dialogService.openDialog(tLabels("accountSection.deleteAccount"), () => {
      return (
        <ContentDialogConfirm
          message={tLabels("accountSection.deleteAccountQuestion")}
          action={() => deleteAccountHandler(account)}
        />
      );
    });
  }
  //#endregion HANDLERS

  //#region SUB-RENDERS
  const renderAccounts = () => {
    return accounts.map((item, index) => 
      <DataGridRow key={index}>
        <AccountSimplified 
          account={item} 
          onDeleteClick={confirmDeleteAccountHandler}
          onDetailClick={onAccountDetailClick}
        />
      </DataGridRow>
    );
  }

  const renderCreateAccountBtn = (): JSX.Element => {
    return (
      <button className="btn btn-sm btn-secondary" onClick={() => setShowAccountDialog(true)}>{tLabels("accountSection.addAccount")}</button>
    );
  }

  const renderNoContentAlert = () => {
    return (
      <Alert alertObj={{ alertType: "Info" }} >
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
          <span className="mr-sm-2 mb-1 mb-sm-0">
            {tLabels("accountSection.noAccountsFound")}
          </span>
          <div>
            {renderCreateAccountBtn()}
          </div>
        </div>
      </Alert>
    )
  }
  //#endregion SUB-RENDERS

  //#region RENDER
  return (
    <>
      <NoContentWrapper
          data={accounts}
          loadStatus={accountsStatus}
          noContentRender={renderNoContentAlert}
          isActiveFilter={isApplyFilter}
      >
        <DataGridHeader
          setShowFilterModal={setShowFilter}
          count={accounts.length}
          actionBtns={renderCreateAccountBtn}
          filterActive={isApplyFilter()}
        />
        <AccountSimplifiedHeader />
        {renderAccounts()}

        <DataFilterModal
          descriptors={descriptors}
          setDescriptors={setDescriptors}
          applyButtonOnClick={filterApplyHandler}
          resetButtonOnClick={filterResetHandler}
          show={showFilter}
          setShow={setShowFilter}
        />
      </NoContentWrapper>
      <InsertAccountDialog 
        show={showAccountDialog}
        setShow={setShowAccountDialog}
        customerId={customerId}
        contracts={contracts}
        contractualAccounts={contractualAccounts.result}
        onSubmit={submitInsertAccountDialogHandler}
      />
    </>
  )
  //#endregion RENDER
}

export default AccountsSection;
